import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Menu, X, MapPin, Phone, Mail, Facebook, Instagram, Twitter, ChevronRight, Waves, Home as HomeIcon, Mountain, FileText, Newspaper, Send, ArrowRight } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logos from the original site
const MAIN_LOGO = "/BBELogoBlack.png";
const FOOTER_LOGO = "/BBELogoBlack.png";
const ESTATE_ILLUSTRATION = "/estate-illustration.jpeg";

// Design images
const HERO_BG = "/IMG_7275.jpeg";
const ABOUT_IMAGE = "/IMG_6859.jpeg";
const LIVING_HERE_IMAGE = "/IMG_6720.jpeg";
const NATURE_IMAGE = "/gm.jpeg";
const NATURE_IMAGE_SURROUNDINGS = "/IMG_6526.jpeg";
const CONTACT_BG = "https://images.unsplash.com/photo-1766169805198-fc1f024cdf1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxnb2xkZW4lMjBob3VyJTIwYmVhY2glMjB3YWxraW5nJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc3NDUxMzI2N3ww&ixlib=rb-4.1.0&q=85";
const SURROUNDINGS_BG = "/surroundings-bg.jpeg";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Available Properties", path: "https://www.property24.com/for-sale/britannia-beach-estate/st-helena-bay/western-cape/33079", external: true },
    { name: "Living Here", path: "/about" },
    { name: "Surroundings", path: "/near-us" },
    { name: "HOA Resources", path: "/resources" },
    { name: "Newsletters", path: "/newsletters" },
    { name: "Contact", path: "/contact" }
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass-header shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between py-4">
            <Link to="/" data-testid="logo-link" className="flex items-center">
              <img
                src={MAIN_LOGO}
                alt="Britannia Beach Estate"
                className="h-[6.75rem] md:h-36 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="nav-link text-sm tracking-wide font-medium transition-colors text-[#1A2524] hover:text-[#2C5E5E]"
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className={`nav-link text-sm tracking-wide font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-[#2C5E5E]"
                        : isScrolled
                        ? "text-[#1A2524] hover:text-[#2C5E5E]"
                        : "text-[#1A2524] hover:text-[#2C5E5E]"
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 text-[#1A2524]"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              data-testid="mobile-menu-close"
              className="absolute top-6 right-6 p-2 text-[#1A2524]"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X size={24} />
            </button>
            <img src={MAIN_LOGO} alt="Britannia Beach Estate" className="h-16 mb-8" />
            {navLinks.map((link, index) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="mobile-menu-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="mobile-menu-link"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer-dark py-16" data-testid="main-footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Tagline */}
          <div className="lg:col-span-2">
            <p className="text-[#F7F5F0]/80 text-lg leading-relaxed max-w-md font-light">
              Where the rhythm of the ocean meets the heartbeat of community
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#A38F7A] text-sm tracking-[0.2em] uppercase mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Living Here", path: "/about" },
                { name: "Surroundings", path: "/near-us" },
                { name: "HOA Resources", path: "/resources" },
                { name: "Contact", path: "/contact" }
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[#A38F7A] text-sm tracking-[0.2em] uppercase mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#A38F7A] mt-1 flex-shrink-0" />
                <span className="text-[#F7F5F0]/70 text-sm">
                  Britannia Beach Estate,<br />
                  Britannia Bay, Western Cape, 7390
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#A38F7A] flex-shrink-0" />
                <a
                  href="tel:+27834767034"
                  data-testid="footer-phone-link"
                  className="text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors text-sm"
                >
                  +27 83 476 7034
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#A38F7A] flex-shrink-0" />
                <a
                  href="mailto:hoa.britanniabeachestate@gmail.com"
                  data-testid="footer-email-link"
                  className="text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors text-sm"
                >
                  hoa.britanniabeachestate@gmail.com
                </a>
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 border border-[#F7F5F0]/30 flex items-center justify-center hover:bg-[#F7F5F0]/10 transition-colors"
              >
                <Facebook size={18} className="text-[#F7F5F0]" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 border border-[#F7F5F0]/30 flex items-center justify-center hover:bg-[#F7F5F0]/10 transition-colors"
              >
                <Instagram size={18} className="text-[#F7F5F0]" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-twitter"
                className="w-10 h-10 border border-[#F7F5F0]/30 flex items-center justify-center hover:bg-[#F7F5F0]/10 transition-colors"
              >
                <Twitter size={18} className="text-[#F7F5F0]" />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-[#F7F5F0]/10 mt-12 pt-8 text-center">
          <p className="text-[#F7F5F0]/50 text-sm">
            © {new Date().getFullYear()} Britannia Beach Estate. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      className="hero-section relative bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_BG})` }}
      data-testid="hero-section"
    >
      <div className="hero-overlay"></div>
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.p
          {...fadeInUp}
          className="text-[#F7F5F0]/90 text-xs md:text-sm tracking-[0.25em] uppercase mb-6"
        >
          Established 2014
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-[#F7F5F0] text-4xl md:text-5xl lg:text-7xl tracking-tight leading-tight mb-6"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Luxury Living at<br />Britannia Beach Estate
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-[#F7F5F0]/80 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Just a breezy one and a half hour drive from Cape Town. Where the rhythm of the ocean meets the heartbeat of community.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="https://www.property24.com/for-sale/britannia-beach-estate/st-helena-bay/western-cape/33079"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-button"
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            Explore Available Properties
            <ArrowRight size={16} />
          </a>
          <Link
            to="/about"
            data-testid="hero-secondary-button"
            className="btn-secondary bg-transparent border-[#F7F5F0] text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A2524] inline-flex items-center justify-center gap-2"
          >
            Discover More
          </Link>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <Waves size={24} className="text-[#F7F5F0]/60" />
      </motion.div>
    </section>
  );
};

// About Preview Section (for Home page)
const AboutPreviewSection = () => {
  return (
    <section className="section-spacing bg-[#F7F5F0]" data-testid="about-preview-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="image-hover-zoom"
          >
            <img
              src="/IMG_2465.jpeg"
              alt="Lifestyle at Britannia Beach Estate"
              className="w-full h-[400px] lg:h-[500px] object-cover"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              Your Coastal Haven
            </p>
            <h2 className="text-[#1A2524] text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight">
              Living at Britannia Beach Estate
            </h2>
            <p className="text-[#4A5655] leading-relaxed mb-6">
              Established in 2014, Britannia Beach Estate stands as a testament to the perfect blend of coastal living and modern convenience. Our growing community offers an idyllic lifestyle within walking distance to the pristine Golden Mile beach.
            </p>
            <p className="text-[#4A5655] leading-relaxed mb-8">
              The verdant landscapes are adorned with available properties for sale, providing a perfect canvas for your coastal dream home. At Britannia Beach Estate, we don't just offer homes; we provide a lifestyle that embodies the essence of coastal living.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-cta"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Learn More
              <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Near Us Bento Section
const NearUsBentoSection = () => {
  return (
    <section className="section-spacing bg-[#FFFFFF]" data-testid="near-us-bento-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
            Discover What's Nearby
          </p>
          <h2 className="text-[#1A2524] text-3xl md:text-4xl lg:text-5xl tracking-tight">
            Why Britannia Beach Estate?
          </h2>
        </motion.div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {/* Large Image - Nature Reserve */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="col-span-12 md:col-span-8 relative h-[300px] md:h-[400px] image-hover-zoom"
          >
            <img
              src={NATURE_IMAGE}
              alt="Great Paternoster Nature Reserve"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-[#F7F5F0]/80 text-xs tracking-[0.15em] uppercase mb-2">Nature & Adventure</p>
              <h3 className="text-[#F7F5F0] text-xl md:text-2xl">Great Paternoster Nature Reserve</h3>
              <p className="text-[#F7F5F0]/70 text-sm mt-2">Hiking, swimming, kayaking, fishing & bird watching</p>
            </div>
          </motion.div>

          {/* Small Card - Paternoster */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="col-span-12 md:col-span-4 bg-[#EBE5D9] p-8 flex flex-col justify-center min-h-[200px] md:min-h-0"
          >
            <p className="text-[#A38F7A] text-xs tracking-[0.15em] uppercase mb-3">10-Minute Drive</p>
            <h3 className="text-[#1A2524] text-xl md:text-2xl mb-2">Paternoster</h3>
            <p className="text-[#4A5655] text-sm">Waterfront charm & Koelkamers theatre</p>
          </motion.div>

          {/* Small Card - West Coast Mall */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="col-span-12 md:col-span-4 bg-[#1A2524] p-8 flex flex-col justify-center min-h-[200px] md:min-h-0"
          >
            <p className="text-[#A38F7A] text-xs tracking-[0.15em] uppercase mb-3">15-Minute Drive</p>
            <h3 className="text-[#F7F5F0] text-xl md:text-2xl mb-2">West Coast Mall</h3>
            <p className="text-[#F7F5F0]/70 text-sm">Via scenic coastal road</p>
          </motion.div>

          {/* Small Card - Golden Mile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="col-span-12 md:col-span-4 bg-[#2C5E5E] p-8 flex flex-col justify-center min-h-[200px] md:min-h-0"
          >
            <p className="text-[#F7F5F0]/60 text-xs tracking-[0.15em] uppercase mb-3">Walking Distance</p>
            <h3 className="text-[#F7F5F0] text-xl md:text-2xl mb-2">Golden Mile Beach</h3>
            <p className="text-[#F7F5F0]/70 text-sm">Pristine sandy shores & rock pools</p>
          </motion.div>

          {/* Small Card - St Helena Bay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="col-span-12 md:col-span-4 bg-[#A38F7A] p-8 flex flex-col justify-center min-h-[200px] md:min-h-0"
          >
            <p className="text-[#1A2524]/60 text-xs tracking-[0.15em] uppercase mb-3">Nearby Town</p>
            <h3 className="text-[#1A2524] text-xl md:text-2xl mb-2">St Helena Bay</h3>
            <p className="text-[#1A2524]/70 text-sm">Spar, shops, restaurants & petrol stations</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/near-us"
            data-testid="near-us-cta"
            className="btn-secondary inline-flex items-center gap-2"
          >
            Explore Surroundings
            <ChevronRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = ({ showTitle = true }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message);
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="section-spacing relative bg-cover bg-center"
      style={{ backgroundImage: `url(${CONTACT_BG})` }}
      data-testid="newsletter-section"
    >
      <div className="absolute inset-0 bg-[#1A2524]/70"></div>
      <div className="container-custom relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          {showTitle && (
            <>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4"
              >
                Stay Connected
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-[#F7F5F0] text-3xl md:text-4xl lg:text-5xl tracking-tight mb-6"
              >
                Subscribe to Our Newsletter
              </motion.h2>
            </>
          )}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[#F7F5F0]/80 mb-8"
          >
            Receive updates on available properties, community news, and upcoming events at Britannia Beach Estate.
          </motion.p>
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              data-testid="newsletter-email-input"
              className="px-6 py-4 bg-[#F7F5F0]/10 border border-[#F7F5F0]/30 text-[#F7F5F0] placeholder-[#F7F5F0]/50 focus:outline-none focus:border-[#F7F5F0] transition-colors w-full sm:w-80"
              required
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              disabled={isSubmitting}
              className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? "Subscribing..." : "Subscribe"}
              <Send size={16} />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <AboutPreviewSection />
      <NearUsBentoSection />
      <NewsletterSection />
    </main>
  );
};

// About Page
const AboutPage = () => {
  return (
    <main className="pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              About Us
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Living at Britannia Beach Estate
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              Established in 2014, Britannia Beach Estate stands as a testament to the perfect blend of coastal living and modern convenience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={LIVING_HERE_IMAGE}
                alt="Estate Lifestyle"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col justify-center"
            >
              <h2 className="text-[#1A2524] text-2xl md:text-3xl mb-6">Your Coastal Haven Near Cape Town</h2>
              <p className="text-[#4A5655] leading-relaxed mb-6">
                Just a breezy one and a half hour drive from Cape Town, our growing community offers an idyllic lifestyle within walking distance to the beach. The verdant landscapes of Britannia Beach Estate are adorned with available properties for sale, providing a perfect canvas for your coastal dream home.
              </p>
              <p className="text-[#4A5655] leading-relaxed">
                At Britannia Beach Estate, we don't just offer homes; we provide a lifestyle that embodies the essence of coastal living. Join us in creating memories, forging friendships, and building your dreams by the sea.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing bg-[#EBE5D9]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <HomeIcon size={32} />,
                title: "Available Properties for Sale",
                description: "Perfect canvas for your coastal dream home with verdant landscapes."
              },
              {
                icon: <Waves size={32} />,
                title: "Lifestyle Focused",
                description: "A lifestyle that embodies the essence of coastal living."
              },
              {
                icon: <MapPin size={32} />,
                title: "Prime Location",
                description: "One and a half hour drive from Cape Town, walking distance to beach."
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="coastal-card text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2C5E5E]/10 text-[#2C5E5E] mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-[#1A2524] text-xl mb-4">{feature.title}</h3>
                <p className="text-[#4A5655]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Estate Illustration */}
      <section className="section-spacing bg-white">
        <div className="container-custom text-center">
          <img
            src={ESTATE_ILLUSTRATION}
            alt="Britannia Beach Estate Illustration"
            className="max-w-4xl mx-auto w-full"
          />
        </div>
      </section>

      <NewsletterSection showTitle={false} />
    </main>
  );
};

// Near Us Page
const NearUsPage = () => {
  const attractions = [
    {
      title: "Golden Mile Beach",
      distance: "Walking Distance",
      description: "Pristine white sandy shores, rock pools, and a sought-after surfing spot. The stunning beach is accessible within walking distance from the estate.",
      image: NATURE_IMAGE
    },
    {
      title: "Great Paternoster Nature Reserve",
      distance: "Immediate Surroundings",
      description: "Nature enthusiasts rejoice in the immediate surroundings offering hiking, swimming, kayaking, fishing, and bird watching.",
      image: NATURE_IMAGE_SURROUNDINGS
    },
    {
      title: "Paternoster",
      distance: "10-Minute Drive",
      description: "The vibrant Paternoster, with its waterfront allure and the renowned Koelkamers theater, offers captivating performances and cultural experiences.",
      image: ABOUT_IMAGE
    },
    {
      title: "West Coast Mall, Vredenburg",
      distance: "15-Minute Drive",
      description: "Embark on a scenic drive along the new coastal road bordered by farmlands. Revel in a top selection of retail outlets, restaurants, pharmacies, and more.",
      image: SURROUNDINGS_BG
    },
    {
      title: "St Helena Bay Town Centre",
      distance: "Nearby",
      description: "Discover the charm of St Helena Bay with a recently renovated Spar, Restaurants, petrol station, and an array of shops. Where if the fish was any fresher they would still be swimming.",
      image: "/IMG_7331.jpeg"
    }
  ];

  return (
    <main className="pt-24" data-testid="near-us-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              Surroundings
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Discover What's Near Us
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              From pristine beaches to vibrant town centres, explore the incredible attractions surrounding Britannia Beach Estate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Attractions */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="space-y-16">
            {attractions.map((attraction, index) => (
              <motion.div
                key={attraction.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="image-hover-zoom">
                    <img
                      src={attraction.image}
                      alt={attraction.title}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-3">
                    {attraction.distance}
                  </p>
                  <h2 className="text-[#1A2524] text-2xl md:text-3xl mb-4">{attraction.title}</h2>
                  <p className="text-[#4A5655] leading-relaxed">{attraction.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSection showTitle={false} />
    </main>
  );
};

// Resources Page
const ResourcesPage = () => {
  const resources = [
    {
      category: "HOA Documents",
      items: [
        { title: "Constitution", description: "Britannia Beach Estate HOA Constitution" },
        { title: "Rules & Regulations", description: "Estate rules and guidelines for residents" },
        { title: "Architectural Guidelines", description: "Building and design standards" },
        { title: "Trustees Report – AGM 21 March 2026", description: "Trustees report presented at the HOA Annual General Meeting", href: "/documents/trustees-report-2026.pdf" }
      ]
    },
    {
      category: "Forms & Applications",
      items: [
        { title: "Building Application", description: "Application form for construction projects" },
        { title: "Landscaping Approval", description: "Request form for garden modifications" },
        { title: "Pet Registration", description: "Register your pets with the HOA" }
      ]
    },
    {
      category: "Useful Information",
      items: [
        { title: "Emergency Contacts", description: "Important contact numbers for emergencies" },
        { title: "Service Providers", description: "Approved contractors and service providers" },
        { title: "Estate Map", description: "Layout and plot locations" }
      ]
    }
  ];

  return (
    <main className="pt-24" data-testid="resources-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              HOA Information
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Resources
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              Access important documents, forms, and information for Britannia Beach Estate residents.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="space-y-16">
            {resources.map((category, catIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-[#1A2524] text-2xl md:text-3xl mb-8 pb-4 border-b border-[#D5CEBF]">
                  {category.category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.items.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="coastal-card group cursor-pointer"
                      onClick={() => item.href && window.open(item.href, '_blank')}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#2C5E5E]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2C5E5E] transition-colors">
                          <FileText size={20} className="text-[#2C5E5E] group-hover:text-white transition-colors" />
                        </div>
                        <div>
                          <h3 className="text-[#1A2524] text-lg mb-2">{item.title}</h3>
                          <p className="text-[#4A5655] text-sm">{item.description}</p>
                          {item.href && <p className="text-[#2C5E5E] text-xs mt-2 font-medium">View Document →</p>}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSection showTitle={false} />
    </main>
  );
};

// Newsletters Page
const NewslettersPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message);
      setEmail("");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-24" data-testid="newsletters-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              Stay Connected
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Newsletters
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              Subscribe to receive the latest updates on available properties, community news, and upcoming events at Britannia Beach Estate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subscription Form */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="coastal-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#2C5E5E]/10 flex items-center justify-center">
                  <Newspaper size={24} className="text-[#2C5E5E]" />
                </div>
                <div>
                  <h2 className="text-[#1A2524] text-2xl">Subscribe to Our Newsletter</h2>
                  <p className="text-[#4A5655] text-sm">Get updates delivered to your inbox</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-[#1A2524] text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="newsletters-email-input"
                    className="coastal-input border border-[#D5CEBF] px-4 py-3"
                    required
                  />
                </div>
                <button
                  type="submit"
                  data-testid="newsletters-submit-button"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? "Subscribing..." : "Subscribe Now"}
                  <Send size={16} />
                </button>
              </form>

              <p className="text-[#4A5655] text-sm mt-6 text-center">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-spacing bg-[#EBE5D9]">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[#1A2524] text-2xl md:text-3xl text-center mb-12"
          >
            What You'll Receive
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Vacant Plot Updates", description: "Be the first to know about new plots for sale" },
              { title: "Community Events", description: "Stay informed about gatherings and activities" },
              { title: "Estate News", description: "Important announcements and updates" }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <h3 className="text-[#1A2524] text-lg mb-2">{item.title}</h3>
                <p className="text-[#4A5655]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              Get in Touch
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Contact Us
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              Have questions about Britannia Beach Estate? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-[#1A2524] text-2xl md:text-3xl mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-[#1A2524] text-sm font-medium mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                    className="coastal-input border border-[#D5CEBF] px-4 py-3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[#1A2524] text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                    className="coastal-input border border-[#D5CEBF] px-4 py-3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[#1A2524] text-sm font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    data-testid="contact-message-input"
                    rows={6}
                    className="coastal-input border border-[#D5CEBF] px-4 py-3 resize-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <Send size={16} />
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:pl-12"
            >
              <h2 className="text-[#1A2524] text-2xl md:text-3xl mb-8">Contact Information</h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C5E5E]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#2C5E5E]" />
                  </div>
                  <div>
                    <h3 className="text-[#1A2524] font-medium mb-1">Address</h3>
                    <p className="text-[#4A5655]">
                      Britannia Beach Estate,<br />
                      Britannia Bay, Western Cape, 7390
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C5E5E]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#2C5E5E]" />
                  </div>
                  <div>
                    <h3 className="text-[#1A2524] font-medium mb-1">Phone</h3>
                    <a
                      href="tel:+27834767034"
                      data-testid="contact-phone-link"
                      className="text-[#2C5E5E] hover:underline"
                    >
                      +27 83 476 7034
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C5E5E]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-[#2C5E5E]" />
                  </div>
                  <div>
                    <h3 className="text-[#1A2524] font-medium mb-1">Email</h3>
                    <a
                      href="mailto:hoa.britanniabeachestate@gmail.com"
                      data-testid="contact-email-link"
                      className="text-[#2C5E5E] hover:underline"
                    >
                      hoa.britanniabeachestate@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="text-[#1A2524] font-medium mb-4">Follow Us</h3>
                <div className="flex items-center gap-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-social-facebook"
                    className="w-12 h-12 border border-[#D5CEBF] flex items-center justify-center hover:bg-[#2C5E5E] hover:border-[#2C5E5E] hover:text-white transition-all text-[#2C5E5E]"
                  >
                    <Facebook size={20} />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-social-instagram"
                    className="w-12 h-12 border border-[#D5CEBF] flex items-center justify-center hover:bg-[#2C5E5E] hover:border-[#2C5E5E] hover:text-white transition-all text-[#2C5E5E]"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-social-twitter"
                    className="w-12 h-12 border border-[#D5CEBF] flex items-center justify-center hover:bg-[#2C5E5E] hover:border-[#2C5E5E] hover:text-white transition-all text-[#2C5E5E]"
                  >
                    <Twitter size={20} />
                  </a>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-12 bg-[#EBE5D9] h-64 flex items-center justify-center">
                <p className="text-[#4A5655]">Map coming soon</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Layout Component
const Layout = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
};

// Available Properties Page
const PlotsPage = () => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [sizeFilter, setSizeFilter] = useState("All");

  const plots = [
    {
      id: 1,
      title: "Plot 14 – Sea View",
      plotNumber: "Erf 14",
      size: 612,
      price: 1250000,
      status: "Available",
      features: ["Sea View", "Corner Plot", "Level Terrain"],
      description: "A prime corner plot with uninterrupted sea views and level terrain — ideal for a single-storey coastal home.",
      image: "https://images.unsplash.com/photo-1605146769289-440113cc3d00?w=800&q=80",
    },
    {
      id: 2,
      title: "Plot 27 – Mountain & Fynbos",
      plotNumber: "Erf 27",
      size: 748,
      price: 980000,
      status: "Available",
      features: ["Mountain View", "Fynbos Border", "Quiet Cul-de-sac"],
      description: "Set on a quiet cul-de-sac bordered by natural fynbos, this generous plot enjoys soft mountain views and plenty of privacy.",
      image: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=800&q=80",
    },
    {
      id: 3,
      title: "Plot 8 – Beach Access",
      plotNumber: "Erf 8",
      size: 530,
      price: 1650000,
      status: "Available",
      features: ["Direct Beach Access", "Sea View", "North Facing"],
      description: "One of the most sought-after plots in the estate — north facing with direct beach access and sweeping ocean vistas.",
      image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
    },
    {
      id: 4,
      title: "Plot 33 – Central & Level",
      plotNumber: "Erf 33",
      size: 695,
      price: 850000,
      status: "Sold",
      features: ["Level Terrain", "Central Location", "Dual Access"],
      description: "A centrally located level plot with dual road access, offering flexibility in home orientation and design.",
      image: "https://images.unsplash.com/photo-1449844908441-8829872d2607?w=800&q=80",
    },
    {
      id: 5,
      title: "Plot 19 – Panoramic Views",
      plotNumber: "Erf 19",
      size: 820,
      price: 1450000,
      status: "Available",
      features: ["Panoramic View", "Large Plot", "Elevated Position"],
      description: "An elevated, generously sized plot with sweeping panoramic views across the estate and towards the Atlantic.",
      image: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=800&q=80",
    },
    {
      id: 6,
      title: "Plot 41 – Fynbos Reserve Edge",
      plotNumber: "Erf 41",
      size: 910,
      price: 1100000,
      status: "Under Offer",
      features: ["Reserve Border", "Extra Large", "Private & Secluded"],
      description: "The largest plot in this section, bordering the private fynbos reserve — exceptional privacy and natural surroundings.",
      image: "https://images.unsplash.com/photo-1518173946687-a4c8892bbd9f?w=800&q=80",
    },
  ];

  const statusColors = {
    Available: "bg-[#2C5E5E] text-white",
    Sold: "bg-[#4A5655] text-white",
    "Under Offer": "bg-[#A38F7A] text-white",
  };

  const filteredPlots = plots.filter((p) => {
    const statusMatch = statusFilter === "All" || p.status === statusFilter;
    const sizeMatch =
      sizeFilter === "All" ||
      (sizeFilter === "Under 600" && p.size < 600) ||
      (sizeFilter === "600–750" && p.size >= 600 && p.size <= 750) ||
      (sizeFilter === "750+" && p.size > 750);
    return statusMatch && sizeMatch;
  });

  const formatPrice = (price) =>
    `R ${price.toLocaleString("en-ZA")}`;

  return (
    <main className="pt-24" data-testid="plots-page">
      {/* Hero */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-4">
              Britannia Beach Estate
            </p>
            <h1 className="text-[#1A2524] text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
              Available Properties For Sale
            </h1>
            <p className="text-[#4A5655] text-lg leading-relaxed">
              Secure your piece of the Western Cape coastline. Each plot is a blank canvas for your dream coastal home, just 90 minutes from Cape Town.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-[#D5CEBF] sticky top-[73px] z-40">
        <div className="container-custom py-4">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-3">
              <span className="text-[#4A5655] text-sm tracking-wide uppercase">Status</span>
              <div className="flex gap-2">
                {["All", "Available", "Under Offer", "Sold"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-4 py-1.5 text-xs tracking-wide uppercase border transition-all ${
                      statusFilter === s
                        ? "bg-[#2C5E5E] text-white border-[#2C5E5E]"
                        : "border-[#D5CEBF] text-[#4A5655] hover:border-[#2C5E5E] hover:text-[#2C5E5E]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#4A5655] text-sm tracking-wide uppercase">Size (m²)</span>
              <div className="flex gap-2">
                {["All", "Under 600", "600–750", "750+"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSizeFilter(s)}
                    className={`px-4 py-1.5 text-xs tracking-wide uppercase border transition-all ${
                      sizeFilter === s
                        ? "bg-[#2C5E5E] text-white border-[#2C5E5E]"
                        : "border-[#D5CEBF] text-[#4A5655] hover:border-[#2C5E5E] hover:text-[#2C5E5E]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <span className="ml-auto text-[#4A5655] text-sm">
              {filteredPlots.length} plot{filteredPlots.length !== 1 ? "s" : ""} found
            </span>
          </div>
        </div>
      </section>

      {/* Listings Grid */}
      <section className="section-spacing bg-[#F7F5F0]">
        <div className="container-custom">
          {filteredPlots.length === 0 ? (
            <div className="text-center py-24 text-[#4A5655]">
              No plots match the selected filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPlots.map((plot, index) => (
                <motion.div
                  key={plot.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white border border-[#D5CEBF] overflow-hidden group hover:shadow-lg transition-shadow"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden h-52">
                    <img
                      src={plot.image}
                      alt={plot.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className={`absolute top-4 left-4 text-xs tracking-[0.15em] uppercase px-3 py-1.5 font-medium ${statusColors[plot.status]}`}>
                      {plot.status}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-[#A38F7A] text-xs tracking-[0.2em] uppercase mb-1">{plot.plotNumber}</p>
                    <h3 className="text-[#1A2524] text-xl mb-1">{plot.title}</h3>
                    <p className="text-[#2C5E5E] text-2xl font-medium mb-4">{formatPrice(plot.price)}</p>

                    {/* Key stats */}
                    <div className="flex gap-6 py-3 border-y border-[#D5CEBF] mb-4">
                      <div className="text-center">
                        <p className="text-[#1A2524] font-medium text-sm">{plot.size} m²</p>
                        <p className="text-[#A38F7A] text-xs uppercase tracking-wide">Plot Size</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[#1A2524] font-medium text-sm">Vacant Land</p>
                        <p className="text-[#A38F7A] text-xs uppercase tracking-wide">Type</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[#1A2524] font-medium text-sm">Britannia Bay</p>
                        <p className="text-[#A38F7A] text-xs uppercase tracking-wide">Location</p>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {plot.features.map((f) => (
                        <span key={f} className="text-xs text-[#4A5655] border border-[#D5CEBF] px-2 py-1">
                          {f}
                        </span>
                      ))}
                    </div>

                    <p className="text-[#4A5655] text-sm leading-relaxed mb-5">{plot.description}</p>

                    <Link
                      to="/contact"
                      className={`w-full text-center block py-3 text-xs tracking-[0.15em] uppercase transition-all ${
                        plot.status === "Available"
                          ? "bg-[#2C5E5E] text-white hover:bg-[#1F4545]"
                          : "bg-[#D5CEBF] text-[#4A5655] cursor-default pointer-events-none"
                      }`}
                    >
                      {plot.status === "Available" ? "Enquire About This Plot" : plot.status}
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <NewsletterSection showTitle={false} />
    </main>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/plots" element={<PlotsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/near-us" element={<NearUsPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/newsletters" element={<NewslettersPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}

export default App;
